import { faGasPump } from '@fortawesome/free-solid-svg-icons/faGasPump';
import Typography from '@mui/material/Typography';
import { AccordionHeaderText } from 'components/atoms/Accordion/AccordionHeaderText';
import { FieldLabelIconMui5 } from 'components/atoms/FieldLabelIcon';
import Accordion from 'components/molecules/Accordion';
import Slider from 'components/molecules/Slider';
import {
  DynamicAuctionFilters,
  DynamicFilterCategory,
} from 'shared/types/api/getAuctions';
import { AccordionTitleContainerMui5 } from '../components';
import OptionFilterSection from './OptionFilterSection';
import { useFilters } from '../../../../../services/filters/hooks/useFilters';
import { DynamicFilter } from '../types';
import {
  isCityFilterValid,
  isHwyFilterValid,
  makeOptionFilterChangeHandler,
  normalizeOptionsFromQuery,
} from 'services/filters/utils';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/system';
import { Theme as ThemeMui5 } from '@mui/material/styles/createTheme';

const dynamicFiltersConfig: DynamicFilter[] = [
  {
    key: DynamicFilterCategory.ExteriorColor,
    title: 'Exterior Color',
    withColor: true,
    withAdornment: false,
  },
  {
    key: DynamicFilterCategory.InteriorColor,
    title: 'Interior Color',
  },
  {
    key: DynamicFilterCategory.Drivetrain,
    title: 'Drivetrain',
    withAdornment: false,
  },
  {
    key: DynamicFilterCategory.BodyStyle,
    title: 'Body Style',
    withAdornment: false,
  },
  {
    key: DynamicFilterCategory.Premium,
    title: 'OEM Options',
  },
  {
    key: DynamicFilterCategory.Transmission,
    title: 'Transmission',
    withAdornment: false,
  },
  {
    key: DynamicFilterCategory.FuelEconomy,
    title: 'Fuel Economy',
    withAdornment: false,
  },
  {
    key: DynamicFilterCategory.FuelType,
    title: 'Fuel Type',
    withAdornment: false,
  },
  {
    key: DynamicFilterCategory.WheelType,
    title: 'Wheel Type',
    optionLabelFormatter: (label) => label.replace('WHEELS: ', ''),
  },
];

type FiltersPropsType = {
  dynamicFilters: DynamicAuctionFilters;
};

const CITY_FILTER_STEP = 1;
const HWY_FILTER_STEP = 1;

const isEqualRange = (a: number[], b: number[]) => {
  return a[0] === b[0] && a[1] === b[1];
};

const accordionContainerStyles: SxProps<ThemeMui5> = (theme) => ({
  padding: 0,
  marginBottom: theme.spacing(2),
});

const accordionBodyStyles: SxProps<ThemeMui5> = (theme) => ({
  padding: theme.spacing(0, 0, 2),
});

const getItemBody = (filter, filters, dynamicFilters, setFilterValue) => (
  <OptionFilterSection
    key={filter.key}
    options={dynamicFilters?.options?.[filter.key]}
    disabled={false}
    optionLabelFormatter={filter.optionLabelFormatter}
    selected={normalizeOptionsFromQuery(filters[filter.key])}
    onChange={(filterKey, name, checked) => {
      makeOptionFilterChangeHandler({
        key: filterKey as DynamicFilterCategory,
        name,
        checked,
        filters,
        set: setFilterValue,
      });
    }}
    filterKey={filter.key}
    withColor={filter.withColor}
    withAdornment={filter.withAdornment}
  />
);

const getFuelEconomyItemBody = (
  showCityFilter,
  showHwyFilter,
  cityEcoValue,
  hwyEcoValue,
  dynamicFilters,
  setFilterValue,
) => {
  if (!showCityFilter || !showHwyFilter) {
    return <Typography>Not available</Typography>;
  }

  return (
    <>
      {showCityFilter && (
        <Box mt={1}>
          <Typography variant='body1'>
            <Typography variant='body1' component='span' noWrap>
              <FieldLabelIconMui5 icon={faGasPump} /> CITY
              (MPG)&nbsp;&nbsp;&nbsp;&nbsp;
            </Typography>
            {cityEcoValue.join(' – ')}
          </Typography>
          <Slider
            name='cityEco'
            value={[...cityEcoValue]}
            valueLabelDisplay='off'
            marks={dynamicFilters.cityEco.map((v) => ({
              value: v,
              label: `${v} MPG`,
            }))}
            min={dynamicFilters.cityEco[0]}
            max={dynamicFilters.cityEco[1]}
            step={CITY_FILTER_STEP}
            track={false}
            onChangeCommitted={(e, value) => {
              if (isEqualRange(value as number[], dynamicFilters.cityEco)) {
                setFilterValue('cityEco', []);
                return;
              }
              if (value[0] + CITY_FILTER_STEP <= value[1]) {
                setFilterValue('cityEco', [...(value as number[])]);
              }
            }}
          />
        </Box>
      )}
      {showHwyFilter && (
        <Box mt={1}>
          <Typography variant='body1'>
            <Typography variant='body1' component='span' noWrap>
              <FieldLabelIconMui5 icon={faGasPump} /> HWY
              (MPG)&nbsp;&nbsp;&nbsp;&nbsp;
            </Typography>
            {hwyEcoValue.join(' – ')}
          </Typography>
          <Slider
            name='hwy'
            value={[...hwyEcoValue]}
            valueLabelDisplay='off'
            marks={dynamicFilters.hwyEco.map((v) => ({
              value: v,
              label: `${v} MPG`,
            }))}
            min={dynamicFilters.hwyEco[0]}
            max={dynamicFilters.hwyEco[1]}
            step={HWY_FILTER_STEP}
            track={false}
            onChangeCommitted={(e, value) => {
              if (isEqualRange(value as number[], dynamicFilters.hwyEco)) {
                setFilterValue('hwyEco', []);
                return;
              }
              if (value[0] + HWY_FILTER_STEP <= value[1]) {
                setFilterValue('hwyEco', [...(value as number[])]);
              }
            }}
          />
        </Box>
      )}
    </>
  );
};

const accordionItemHeaderStyles: SxProps<ThemeMui5> = () => ({
  '&.MuiAccordionSummary-root': {
    backgroundColor: 'transparent !important',
  },
});

export const AccordionFilters = ({ dynamicFilters }: FiltersPropsType) => {
  const { filters, setFilterValue } = useFilters();

  const showCityFilter = isCityFilterValid(dynamicFilters);
  const showHwyFilter = isHwyFilterValid(dynamicFilters);

  const cityEcoValue = filters.cityEco?.length
    ? filters.cityEco
    : dynamicFilters.cityEco;

  const hwyEcoValue = filters.hwyEco?.length
    ? filters.hwyEco
    : dynamicFilters.hwyEco;

  const items = dynamicFiltersConfig
    .map((filter) => ({
      id: filter.key,
      header: {
        text: (
          <AccordionTitleContainerMui5>
            <AccordionHeaderText>{filter.title}</AccordionHeaderText>
          </AccordionTitleContainerMui5>
        ),
        sx: accordionItemHeaderStyles,
      },
      body:
        filter.key === DynamicFilterCategory.FuelEconomy
          ? getFuelEconomyItemBody(
              showCityFilter,
              showHwyFilter,
              cityEcoValue,
              hwyEcoValue,
              dynamicFilters,
              setFilterValue,
            )
          : getItemBody(filter, filters, dynamicFilters, setFilterValue),
    }))
    .filter(Boolean);

  return (
    <Accordion
      containerSx={accordionContainerStyles}
      bodySx={accordionBodyStyles}
      defaultExpanded={false}
      TransitionProps={{ unmountOnExit: false }}
      items={items}
    />
  );
};
